/**
 * Moving an existing inventory out of localStorage, once.
 *
 * The rule this is written around: **nothing is deleted until it is provably
 * somewhere else.** The old key is read, written to IndexedDB, read *back* out
 * of IndexedDB, and only then marked done — and even then the original is left
 * in place rather than removed. localStorage holds a few hundred KB of text and
 * someone's entire food inventory is not worth reclaiming it for.
 *
 * A migration that half-succeeds is the specific disaster to avoid, because the
 * user would have no way to tell. So it either completes or it leaves
 * everything exactly as it was and runs again next time.
 */

import { db, hasIndexedDb } from "./db";
import { hydrateItems } from "./itemStore";
import { readLegacyCache } from "../../features/vision/recognitionCache";
import type { FoodItem } from "../../features/food/types";

export const LEGACY_ITEMS_KEY = "pantrymind.foodItems.v1";
export const LEGACY_VISION_KEY = "pantrymind.vision.v1";
/** Set only after the copy has been read back and verified. */
export const MIGRATED_FLAG = "pantrymind.migrated.idb.v1";

function readLegacy(key: string): unknown {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as unknown) : null;
  } catch {
    return null;
  }
}

/** Enough of an item to be worth keeping. Same test the old reader used. */
function looksLikeItem(value: unknown): value is FoodItem {
  return typeof value === "object" && value !== null && typeof (value as FoodItem).id === "string";
}

export interface MigrationResult {
  /** "skipped" when there was nothing to do or nowhere to put it. */
  status: "migrated" | "skipped" | "failed";
  items: number;
  cached: number;
}

export async function migrateFromLocalStorage(): Promise<MigrationResult> {
  if (!hasIndexedDb()) return { status: "skipped", items: 0, cached: 0 };

  try {
    if (localStorage.getItem(MIGRATED_FLAG)) {
      return { status: "skipped", items: 0, cached: 0 };
    }
  } catch {
    return { status: "skipped", items: 0, cached: 0 };
  }

  const legacyItems = readLegacy(LEGACY_ITEMS_KEY);
  const items = Array.isArray(legacyItems) ? legacyItems.filter(looksLikeItem) : [];

  // Through the cache's own reader, which understands both shapes it has had —
  // a list per photo, and the single object it used before shelf scanning.
  const cacheEntries = readLegacyCache();

  try {
    const database = await db();

    if (items.length > 0) {
      const tx = database.transaction(["items", "photos"], "readwrite");
      for (const item of items) {
        const { photo, ...row } = item;
        await tx.objectStore("items").put(row);
        if (typeof photo === "string" && photo) {
          await tx.objectStore("photos").put({ id: item.id, photo });
        }
      }
      await tx.done;

      // Read back before believing it. A transaction that resolved is not the
      // same claim as "the data is there", and this is the one moment where
      // being wrong costs someone their inventory.
      const written = await database.count("items");
      if (written < items.length) {
        return { status: "failed", items: 0, cached: 0 };
      }
    }

    let cached = 0;
    if (cacheEntries.length > 0) {
      const tx = database.transaction("vision", "readwrite");
      for (const entry of cacheEntries) {
        await tx.store.put({ hash: entry.hash, results: entry.results, at: Date.now() });
        cached += 1;
      }
      await tx.done;
    }

    // The old keys are deliberately **not** removed. They cost a few hundred KB
    // and they are the only copy that predates this code; if something about
    // the new store turns out to be wrong, they are the way back.
    localStorage.setItem(MIGRATED_FLAG, new Date().toISOString());

    // Whatever just landed has to be in memory before the app renders.
    await hydrateItems();

    return { status: "migrated", items: items.length, cached };
  } catch {
    return { status: "failed", items: 0, cached: 0 };
  }
}
